/**
 * Filtros das páginas vivem na URL (critério do CLAUDE.md: links compartilháveis).
 *
 * `useFiltrosUrl(padroes)` devolve os valores atuais (URL ou padrão) e um setter que
 * atualiza a query string. Valores iguais ao padrão saem da URL, para mantê-la curta.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

export type ValorFiltro = string | number | boolean;
export type Filtros = Record<string, ValorFiltro>;

export function lerFiltros<T extends Filtros>(busca: URLSearchParams, padroes: T): T {
  const resultado: Filtros = { ...padroes };
  for (const [chave, padrao] of Object.entries(padroes)) {
    const bruto = busca.get(chave);
    if (bruto === null) continue;
    if (typeof padrao === 'number') {
      const numero = Number(bruto);
      if (!Number.isNaN(numero)) resultado[chave] = numero;
    } else if (typeof padrao === 'boolean') {
      resultado[chave] = bruto === 'true' || bruto === '1';
    } else {
      resultado[chave] = bruto;
    }
  }
  return resultado as T;
}

export function escreverFiltros<T extends Filtros>(
  busca: URLSearchParams,
  padroes: T,
  mudancas: Partial<T>,
): URLSearchParams {
  const nova = new URLSearchParams(busca);
  for (const [chave, valor] of Object.entries(mudancas)) {
    if (valor === undefined || valor === padroes[chave]) nova.delete(chave);
    else nova.set(chave, String(valor));
  }
  return nova;
}

export function useFiltrosUrl<T extends Filtros>(padroes: T) {
  const [busca, setBusca] = useSearchParams();
  // `padroes` costuma ser um literal; serializar evita recalcular a cada render.
  const chavePadroes = JSON.stringify(padroes);
  const filtros = useMemo(
    () => lerFiltros(busca, JSON.parse(chavePadroes) as T),
    [busca, chavePadroes],
  );
  const definir = useCallback(
    (mudancas: Partial<T>) =>
      setBusca((atual) => escreverFiltros(atual, JSON.parse(chavePadroes) as T, mudancas), {
        replace: true,
      }),
    [setBusca, chavePadroes],
  );
  return [filtros, definir] as const;
}
